import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import { styled } from '@mui/system'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../store/redux/hooks'
import { getDailyDate, getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { RootState } from '../../store/redux/store'
import DateFormat from '../../store/types/enums/DateFormat'

const StyledMonthPicker = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
}))

const StyledMonthAndYearContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isWeekly' && prop !== 'isDaily',
})<{ isWeekly?: boolean; isDaily?: boolean }>(({ isWeekly, isDaily }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: isWeekly ? 150 : isDaily ? 110 : 65,
}))

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 28,
  height: 28,
  '& svg': {
    color: theme.palette.text.mediumEmphasis,
  },
  '&:hover': {
    svg: {
      color: theme.palette.text.highEmphasis,
    },
  },
}))

const getDateToShow = (selectedDate: dayjs.Dayjs, isWeeklyRange: boolean, isDailyRange: boolean) => {
  if (isWeeklyRange) {
    const startOfWeek = selectedDate.startOf('week')
    const endOfWeek = startOfWeek.add(6, 'days')
    const isDifferentMonth = startOfWeek.month() !== endOfWeek.month()

    return isDifferentMonth
      ? `${startOfWeek.format('D MMMM')} - ${endOfWeek.format('D MMMM')}`
      : `${startOfWeek.format('D')} - ${endOfWeek.format('D MMMM')}`
  }
  return isDailyRange ? `${selectedDate.format('D MMMM')}` : `${selectedDate.format(DateFormat.MonthName)}`
}

type MonthPickerProps = {
  isWeeklyRange?: boolean
  isDailyRange?: boolean
  defaultDate?: string | Dayjs
}

const MonthPicker = ({ isWeeklyRange = false, isDailyRange = false, defaultDate }: MonthPickerProps) => {
  const selector = isDailyRange ? getDailyDate : getSelectedDate
  const rawDate = dayjs(useSelector((state: RootState) => selector(state)))
  const selectedDate = rawDate.isValid() ? rawDate : dayjs()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (defaultDate) {
      if (isDailyRange) dispatch(monthPickerActions.setDailyDate(dayjs(defaultDate).toString()))
      else dispatch(monthPickerActions.setDate(dayjs(defaultDate).toString()))
    }
  }, [defaultDate, isDailyRange, dispatch])

  const handlePrevious = () => {
    if (isWeeklyRange) dispatch(monthPickerActions.decreaseWeek())
    else if (isDailyRange) dispatch(monthPickerActions.decreaseDailyDay())
    else dispatch(monthPickerActions.decreaseMonth())
  }

  const handleNext = () => {
    if (isWeeklyRange) dispatch(monthPickerActions.increaseWeek())
    else if (isDailyRange) dispatch(monthPickerActions.increaseDailyDay())
    else dispatch(monthPickerActions.increaseMonth())
  }

  return (
    <StyledMonthPicker>
      <StyledIconButton aria-label="Previous" size="small" onClick={handlePrevious}>
        <ChevronLeft />
      </StyledIconButton>

      <StyledMonthAndYearContainer isWeekly={isWeeklyRange} isDaily={isDailyRange}>
        <Typography
          variant="body1"
          fontWeight={700}
          sx={{ color: 'text.highEmphasis', textTransform: 'capitalize', textAlign: 'center' }}
        >
          {getDateToShow(selectedDate, isWeeklyRange, isDailyRange)}
        </Typography>
        <Typography variant="subtitle2" component="p" fontWeight={400} sx={{ color: 'text.base' }}>
          {selectedDate.format(DateFormat.Year)}
        </Typography>
      </StyledMonthAndYearContainer>

      <StyledIconButton aria-label="Next" size="small" onClick={handleNext}>
        <ChevronRight />
      </StyledIconButton>
    </StyledMonthPicker>
  )
}

export default MonthPicker
